import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { IMG } from "@/data/content";
import { ArrowLeft, Calendar, Share2, Facebook, Twitter, Linkedin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; }
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const { data } = await axios.get(`${API}/blog/${slug}`);
        setPost(data.post);
        setRelated(data.related || []);
        // Update document meta for SEO
        if (data.post) {
          document.title = data.post.meta_title || `${data.post.title} — Geomantic Kalpataru`;
          const setMeta = (name, content) => {
            let el = document.querySelector(`meta[name="${name}"]`);
            if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
            el.setAttribute("content", content || "");
          };
          const setOg = (property, content) => {
            let el = document.querySelector(`meta[property="${property}"]`);
            if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
            el.setAttribute("content", content || "");
          };
          setMeta("description", data.post.meta_description || data.post.excerpt || "");
          setOg("og:title", data.post.meta_title || data.post.title);
          setOg("og:description", data.post.meta_description || data.post.excerpt || "");
          setOg("og:image", data.post.og_image || data.post.featured_image || "");
          setOg("og:type", "article");
        }
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
    return () => { document.title = "Geomantic Kalpataru — Ancient Manifestation, Modern Awakening"; };
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-ivory/55 pt-40">Loading essay…</div>;
  if (notFound || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 pt-40 pb-20">
        <h1 className="font-display text-3xl text-ivory">Essay not found</h1>
        <Link to="/blog" className="btn-gold">Back to Journal</Link>
      </div>
    );
  }

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  // Social crawlers can't run JS. Use a backend share-preview endpoint that
  // serves per-post Open Graph meta tags and redirects real users to the blog URL.
  const shareUrl = post?.slug
    ? `${process.env.REACT_APP_BACKEND_URL}/api/share/blog/${post.slug}`
    : currentUrl;
  const share = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${post.title} — ${shareUrl}`)}`,
  };

  return (
    <div data-testid="blog-detail-page">
      <PageHero
        eyebrow={(post.categories || [])[0] || "Journal"}
        title={<>{post.title}</>}
        subtitle={post.subtitle || post.excerpt}
        image={post.featured_image || IMG.ancientBook}
      />

      <section className="section">
        <div className="wrap max-w-3xl min-w-0 overflow-hidden">
          <div className="text-xs uppercase tracking-[0.25em] text-ivory/55 flex flex-wrap items-center gap-3 break-words">
            <span>— {post.author || "The Studio"}</span>
            <span className="text-gold/40">·</span>
            <Calendar className="h-3 w-3 text-gold" /> {formatDate(post.publish_at || post.created_at)}
          </div>

          <article
            className="prose-kalpataru mt-10 text-ivory/85 leading-relaxed min-w-0 max-w-full overflow-hidden"
            style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body_html || "") }}
          />

          {(post.tags || []).length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-[0.25em] text-gold border border-gold/30 px-3 py-1">
                  #{t}
                </span>
              ))}
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-gold/15 flex flex-wrap items-center gap-4">
            <span className="text-xs uppercase tracking-[0.25em] text-ivory/55 flex items-center gap-2">
              <Share2 className="h-3.5 w-3.5" /> Share
            </span>
            {[
              { Icon: Facebook, href: share.facebook, label: "facebook" },
              { Icon: Twitter, href: share.twitter, label: "twitter" },
              { Icon: Linkedin, href: share.linkedin, label: "linkedin" },
            ].map(({ Icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                 data-testid={`share-${label}`}
                 className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold hover:text-midnight transition">
                <Icon className="h-4 w-4" strokeWidth={1.5} />
              </a>
            ))}
            <a href={`https://www.threads.net/intent/post?text=${encodeURIComponent(`${post.title} — via @agnapushpanjal — ${shareUrl}`)}`}
               target="_blank" rel="noreferrer" data-testid="share-threads"
               className="text-xs uppercase tracking-[0.25em] text-gold hover:text-saffron transition">
              Threads
            </a>
            <a href={share.whatsapp} target="_blank" rel="noreferrer" data-testid="share-whatsapp"
               className="text-xs uppercase tracking-[0.25em] text-gold hover:text-saffron transition">
              WhatsApp
            </a>
          </div>

          <div className="mt-14">
            <Link to="/blog" className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em]">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Journal
            </Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section border-t border-gold/10 bg-midnight-paper/40">
          <div className="wrap">
            <div className="eyebrow mb-4">Related essays</div>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="glass lift overflow-hidden group" data-testid={`related-blog-${r.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={r.featured_image || IMG.ancientBook} alt={r.title} className="h-full w-full object-cover transition duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-xl text-ivory">{r.title}</h3>
                    {r.excerpt && <p className="mt-2 text-sm text-ivory/60 line-clamp-2">{r.excerpt}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetail;
